import styled from '@emotion/styled'
import { Form, OTPField, PhoneNumberField, SwitchField, TextField } from '.'
import { Button, CircularProgress } from '@mui/material'
import SVG from '../../assets/svg'
import React, { useEffect } from 'react'
import SelectField from './SelectField'
import { color, screen } from '../../styles'

interface SingleFieldFormProps {
   initialValue?: string | number | boolean | null
   onSubmit: (value: { field: string | number | boolean | null }) => void
   type: 'text' | 'number' | 'otp' | 'phone' | 'switch' | 'select' | 'email'
   selectionOptions?: { value: string | number; label: string }[]
   placeholder?: string
   submitButtonText?: string
   disabled?: boolean
   loading: boolean
   onFormFocus?: () => void
   onChange?: (value: string | number | boolean | null) => void
   otpFieldSettings?: {
      numberOfCharacters: number
      gap?: number
   }
   resetForm?: boolean
}
const SingleFieldForm: React.FC<SingleFieldFormProps> = (props) => {
   const {
      initialValue,
      onSubmit,
      placeholder,
      loading,
      disabled,
      type,
      submitButtonText,
      onFormFocus,
      onChange,
      otpFieldSettings,
      resetForm,
   } = props

   const isTextField = type === 'text' || type === 'number' || type === 'phone' || type === 'email'

   return (
      <Holder>
         <Form
            onSubmit={onSubmit}
            initialValues={{ field: initialValue || undefined }}
            onFormFocus={onFormFocus && onFormFocus}
            error={false}
            loading={loading}
         >
            {({ values, resetForm: runResetForm }) => {
               // this useEffect is used to call onChange prop when the value of field changes
               useEffect(() => {
                  onChange && onChange(values.field)
               }, [values.field])

               // this useEffect is used to reset the form when resetForm prop is true
               useEffect(() => {
                  resetForm && runResetForm()
               }, [resetForm])

               // this function is used to check if all the characters are entered in OTP field
               const isAllOTPCharactersEntered = () => {
                  if (values.field) {
                     if (values.field.length === otpFieldSettings?.numberOfCharacters) {
                        return true
                     }
                  }
                  return false
               }

               // ===
               // JSX
               // ===
               return (
                  <>
                     {isTextField && (
                        <div className="text-inputs">
                           {type === 'phone' ? (
                              <PhoneNumberField
                                 name="field"
                                 disabled={disabled}
                                 placeholder={placeholder}
                              />
                           ) : (
                              <TextField
                                 name="field"
                                 type={type || 'text'}
                                 disabled={disabled}
                                 placeholder={placeholder}
                                 style={{ width: '100%' }}
                              />
                           )}
                           <Button
                              type="submit"
                              size="large"
                              variant="contained"
                              endIcon={
                                 loading ? (
                                    <CircularProgress size={'100%'} />
                                 ) : (
                                    <SVG.form.send className="send-icon" />
                                 )
                              }
                              disabled={loading || disabled}
                           >
                              {submitButtonText}
                           </Button>
                        </div>
                     )}
                     {type === 'otp' && (
                        <div className="otp-input">
                           <OTPField
                              name="field"
                              numberOfCharacters={otpFieldSettings?.numberOfCharacters || 6}
                              gap={otpFieldSettings?.gap || 2}
                              onComplete={(value) => onSubmit({ field: value })}
                              disabled={disabled || loading}
                           />
                           <Button
                              type="submit"
                              size="large"
                              variant="contained"
                              disabled={loading || disabled || !isAllOTPCharactersEntered()}
                           >
                              {loading ? 'Verifying...' : 'Verify'}
                           </Button>
                        </div>
                     )}
                     {type === 'switch' && (
                        <SwitchField
                           name="field"
                           disabled={disabled}
                           size="medium"
                           label={placeholder}
                        />
                     )}
                     {type === 'select' && (
                        <div className="single_filed_form-select-field">
                           <SelectField
                              label=""
                              name="field"
                              options={props.selectionOptions || []}
                              disabled={disabled}
                              placeholder={placeholder}
                              onChange={({ target }) =>
                                 onSubmit({ field: target.value as string | number })
                              }
                           />
                        </div>
                     )}
                  </>
               )
            }}
         </Form>
      </Holder>
   )
}

const Holder = styled.div`
   --border-color: #e0e0e0;
   --border-radius: 30px;
   width: 100%;

   form {
      background-color: #fff;
      border-radius: var(--border-radius);
   }

   .MuiFormControl-root {
      margin: 0;
      border-radius: var(--border-radius);
      overflow: hidden;
   }

   .text-inputs {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-block: 0;
      border: 2px solid var(--border-color);
      border-radius: var(--border-radius);
      padding: 6px;
      gap: 6px;
      transition: border-color 0.2s ease-in-out;

      &:hover {
         --border-color: ${color.grayLight};
      }

      &:focus-within {
         --border-color: ${color.green};
      }

      fieldset {
         display: none;
      }
      input {
         border: 2px solid #e0e0e0;
         background-color: #fff;
         border-right: none;
         border: none;
         border-radius: var(--border-radius);
         box-sizing: border-box;
         padding-block: 1.4em;
         box-shadow: none !important;
      }

      .react-tel-input {
         input {
            padding-block: 0.8em;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            overflow: initial;
            border-radius: var(--border-radius);
         }
         &:focus-within .form-control,
         &:focus .form-control {
            border: none !important;
            box-shadow: none !important;
         }
         .flag-dropdown {
            border-right-width: 2px;
         }

         &:hover .flag-dropdown {
            border-right-color: ${color.grayLight};
         }
         &:focus-within .flag-dropdown {
            box-shadow: none !important;
         }
      }
      button {
         display: flex;
         align-items: center;
         margin: 0;
         box-shadow: none !important;
         padding-block: 0.5em;
         border-radius: var(--border-radius);

         svg {
            width: 1.3rem;
            height: 1.3rem;
         }

         .MuiCircularProgress-svg {
            circle {
               stroke: #fff !important;
            }
         }
      }
   }

   .otp-input {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-block: 0;
      gap: 0.9em;

      .MuiFormControl-root {
         margin-block: 0;
         border-radius: 2px;
      }
      input {
         padding-block: 0.25em;
      }

      .MuiOtpInput-Box {
         ${screen.onlyMobile} {
            gap: 3.4vw;
         }
      }

      button {
         margin-block: 0;
         width: 100%;
      }
   }

   .single_filed_form-select-field {
      height: 100%;
      border-radius: 0;
      isolation: isolate;
      .MuiSelect-select {
         margin: 0;
      }

      fieldset {
         border-radius: var(--border-radius);
         overflow: hidden;
         height: 100%;
      }
   }
`

export default SingleFieldForm
export type { SingleFieldFormProps }
